/**
 * Shared window state machine for history charts (asset + tracker).
 *
 * Two distinct concepts:
 *  - RANGE: an explicit user-selected span (the From/To pickers). It is what gets
 *    fetched, and it bounds navigation. `null` means "no explicit range" — the
 *    chart follows the caller's configured window and pans freely, one view at a
 *    time, re-fetching as it goes (the original behaviour).
 *  - VIEW: the window the chart actually draws. Its width is the period selector;
 *    ←/→ pan it. Inside a range it is clamped to the range and needs no re-fetch,
 *    because the range's data is already loaded.
 *
 * Position is stored as an ABSOLUTE start time, never as a multiple of the period
 * — a period-denominated offset silently rescales when the period changes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DateTime } from 'luxon'

import { useApi } from '@/hooks'
import type { ApiService } from '@/types/dashboard-types'

import type { PeriodWindow } from './timeAxis'

/** An absolute window. `to` is exclusive. */
export interface HistoryWindow {
  from: DateTime
  to: DateTime
}

interface UseHistoryNavigationArgs<TData> {
  /** Parent-provided data for the configured window. */
  data: TData | undefined
  /** Configured window start (already computed by the loader). */
  from: DateTime
  periodHours: number
  /** User profile timezone (normalized). */
  zone: string
  /** Fetch an explicit window. */
  reload: (api: ApiService, window: HistoryWindow) => Promise<TData | undefined>
  /**
   * Range to start in (e.g. a shipment's actual bounds). The pickers show it and
   * reset returns to it. Must be referentially stable (memoised by the caller);
   * when given, the parent's `data` is assumed to cover it.
   */
  defaultRange?: HistoryWindow | null
  /**
   * Window to open on instead of the configured one, panning freely from there (e.g. the
   * period another tab was left on). Read once, on mount; reset still returns to `from`.
   */
  initialWindow?: HistoryWindow
  /** Told the fetched window (range, or else view) whenever it changes, and once on mount. */
  onWindowChange?: (window: HistoryWindow) => void
  /** Told the drawn window whenever it changes, and once on mount, so a chart beside this
   *  one can follow its navigation. */
  onViewChange?: (window: HistoryWindow) => void
}

const windowKey = (w: HistoryWindow | null): string => (w ? `${w.from.toMillis()}|${w.to.toMillis()}` : '')

function clampViewStart(candidate: DateTime, range: HistoryWindow, viewHours: number): DateTime {
  const latest = range.to.minus({ hours: viewHours })
  if (latest < range.from) return range.from
  if (candidate < range.from) return range.from
  return candidate > latest ? latest : candidate
}

export function useHistoryNavigation<TData>({
  data,
  from,
  periodHours,
  zone,
  reload,
  defaultRange,
  initialWindow,
  onWindowChange,
  onViewChange,
}: UseHistoryNavigationArgs<TData>) {
  const api: ApiService = useApi().api
  const [currentData, setCurrentData] = useState<TData | undefined>(data)
  const [loading, setLoading] = useState(!data)

  const baseRange = defaultRange ?? null
  const [range, setRange] = useState<HistoryWindow | null>(baseRange)
  const [viewHours, setViewHours] = useState(() =>
    initialWindow ? initialWindow.to.diff(initialWindow.from, 'hours').hours : periodHours
  )
  const [viewStart, setViewStart] = useState<DateTime>(initialWindow?.from ?? from)

  const viewWindow = useMemo<HistoryWindow>(
    () => ({ from: viewStart, to: viewStart.plus({ hours: viewHours }) }),
    [viewStart, viewHours]
  )

  const fetchWindow = range ?? viewWindow
  const fetchKey = windowKey(fetchWindow)

  const reloadRef = useRef(reload)
  const fetchWindowRef = useRef(fetchWindow)
  const onWindowChangeRef = useRef(onWindowChange)
  const viewWindowRef = useRef(viewWindow)
  const onViewChangeRef = useRef(onViewChange)
  useEffect(() => {
    reloadRef.current = reload
    fetchWindowRef.current = fetchWindow
    onWindowChangeRef.current = onWindowChange
    viewWindowRef.current = viewWindow
    onViewChangeRef.current = onViewChange
  }, [reload, fetchWindow, onWindowChange, viewWindow, onViewChange])

  useEffect(() => {
    onWindowChangeRef.current?.(fetchWindowRef.current)
  }, [fetchKey])

  const viewKey = windowKey(viewWindow)
  useEffect(() => {
    onViewChangeRef.current?.(viewWindowRef.current)
  }, [viewKey])

  /** The window the parent's `data` prop already covers; no fetch needed for it. */
  const parentKey = windowKey(baseRange ?? { from, to: from.plus({ hours: periodHours }) })
  const parentKeyRef = useRef(parentKey)

  const baselineKey = `${from.toMillis()}|${periodHours}|${windowKey(baseRange)}`
  const prevBaselineRef = useRef(baselineKey)
  useEffect(() => {
    if (prevBaselineRef.current === baselineKey) return
    prevBaselineRef.current = baselineKey
    parentKeyRef.current = parentKey
    setRange(baseRange)
    setViewHours(periodHours)
    setViewStart(from)
    if (data) {
      setCurrentData(data)
      setLoading(false)
    }
  }, [baselineKey, parentKey, baseRange, from, periodHours, data])

  useEffect(() => {
    if (!api) return
    if (fetchKey === parentKeyRef.current) {
      if (data) {
        setCurrentData(data)
        setLoading(false)
      }
      return
    }
    // Race guard: a newer window's fetch must win, and a failed fetch must never
    // blank the chart (keep showing whatever is on screen).
    let cancelled = false
    setLoading(true)
    reloadRef.current(api, fetchWindowRef.current).then((next) => {
      if (cancelled) return
      if (next) setCurrentData(next)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [api, fetchKey, data])

  const periodWindow: PeriodWindow = useMemo(() => [viewWindow.from.toMillis(), viewWindow.to.toMillis()], [viewWindow])

  const navigate = useCallback(
    (direction: -1 | 1) => {
      setViewStart((prev) => {
        const next = prev.plus({ hours: direction * viewHours })
        return range ? clampViewStart(next, range, viewHours) : next
      })
    },
    [range, viewHours]
  )

  /** Period selector: changes the view width only, never the fetched range. */
  const changeViewHours = useCallback(
    (hours: number) => {
      if (!Number.isFinite(hours) || hours <= 0) return
      const capped = range ? Math.min(hours, range.to.diff(range.from, 'hours').hours) : hours
      setViewHours(capped)
      setViewStart((prev) => (range ? clampViewStart(prev, range, capped) : prev))
    },
    [range]
  )

  /** From/To pickers. Passing null drops back to the configured window. */
  const applyRange = useCallback(
    (next: HistoryWindow | null) => {
      if (!next) {
        setRange(baseRange)
        setViewHours(periodHours)
        setViewStart(from)
        return
      }
      const hours = next.to.diff(next.from, 'hours').hours
      if (hours <= 0) return
      setRange(next)
      setViewHours(hours)
      setViewStart(next.from)
    },
    [baseRange, from, periodHours]
  )

  /** Date-only jump (tracker's single picker). Moves the view, never the range. */
  const jumpToDate = useCallback(
    (picked: Date | null) => {
      if (!picked) return
      const target = DateTime.fromObject(
        {
          year: picked.getFullYear(),
          month: picked.getMonth() + 1,
          day: picked.getDate(),
          hour: picked.getHours(),
          minute: picked.getMinutes(),
        },
        { zone }
      )
      if (!target.isValid) return
      setViewStart(range ? clampViewStart(target, range, viewHours) : target)
    },
    [range, viewHours, zone]
  )

  const resetToDefault = useCallback(() => {
    applyRange(null)
  }, [applyRange])

  const isModified = windowKey(range) !== windowKey(baseRange) || !viewStart.equals(from) || viewHours !== periodHours

  return {
    currentData,
    loading,
    periodWindow,
    range,
    viewHours,
    isModified,
    navigate,
    changeViewHours,
    applyRange,
    jumpToDate,
    resetToDefault,
  }
}
